import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popup } from 'semantic-ui-react';

import {
    checkDate,
    format,
    getDateRegex,
    isBefore,
    isSameDay,
    isSameDayOrBefore,
    isSameOrBefore,
    parse
} from '../../services/dateUtils';
import ClearableInput from '../Input/ClearableInput';
import DatePicker from './DatePicker';

import { StyledRangeDatePicker } from './DatePicker.styles';

const RangeDatePicker = ({
    dateFromName = 'from',
    dateFromValue,
    dateToName = 'to',
    dateToValue,
    pattern = 'dd.MM.yy',
    placeholder = 'Выберите период',
    label,
    onChange,
    withSort,
    showMonthDropdown = true,
    showYearDropdown = true,
    dropdownMode = 'select',
    ...props
}) => {
    const [open, setOpen] = useState(false);
    const [hoverDate, setHoverDate] = useState(null);
    const latestSelectionRef = useRef({ from: dateFromValue, to: dateToValue });
    const isSelectingSecondDate = !!(dateFromValue && !dateToValue && checkDate(dateFromValue));

    const {
        dateRegex,
        regex
    } = useMemo(
        () => {
            const dateRegex = getDateRegex(pattern);
            /* eslint-disable-next-line */
            const regex = new RegExp(`^${dateRegex.source}.*${dateRegex.source}\$`);

            return {
                dateRegex,
                regex
            };
        },
        [pattern]
    );

    const bothDatesAreFilled = !!(dateFromValue && dateToValue);

    const text = useMemo(
        () => {
            if (!bothDatesAreFilled) {
                if (dateFromValue && checkDate(dateFromValue)) {
                    return `${format(dateFromValue, pattern)} - `;
                }

                if (dateToValue && checkDate(dateToValue)) {
                    return ` - ${format(dateToValue, pattern)}`;
                }

                return '';
            }

            return [dateFromValue, dateToValue]
                .map(date => checkDate(date)
                    ? format(date, pattern)
                    : '...')
                .join(' - ');
        },
        [
            bothDatesAreFilled,
            dateFromValue,
            dateToValue,
            pattern
        ]
    );

    const [inputValue, setInputValue] = useState(text);

    const handleTextInput = useCallback(
        (e, { value }) => {
            setInputValue(value);

            if (!value && e.type === 'click') {
                setOpen(false);
                setHoverDate(null);
                latestSelectionRef.current = { from: '', to: '' };

                onChange({
                    [dateFromName]: '',
                    [dateToName]: ''
                });

                return;
            }

            const str = (value || '').trim();

            if (!value || !regex.test(str)) return;

            const [from, to] = str.match(dateRegex);

            if (!(from && to)) return;

            const dateFrom = parse(from, pattern);
            const dateTo = parse(to, pattern);

            if (!(checkDate(dateFrom) && checkDate(dateTo) && isSameOrBefore(dateFrom, dateTo))) return;

            latestSelectionRef.current = { from: dateFrom, to: dateTo };

            onChange({
                [dateFromName]: dateFrom,
                [dateToName]: dateTo
            });
        },
        [
            pattern,
            regex,
            dateRegex,
            dateFromName,
            dateToName,
            onChange
        ]
    );

    const handleChange = useCallback(
        (e, { value }) => {
            e.stopPropagation();

            let [dateFrom, dateTo] = value || [];
            const prev = latestSelectionRef.current;

            // selectsRange сбрасывает выбор, если вторая дата раньше первой.
            // Разрешаем порядок «сначала ДО, потом ОТ»: меняем даты местами.
            if (
                dateFrom &&
                !dateTo &&
                prev.from &&
                !prev.to &&
                checkDate(prev.from) &&
                isBefore(dateFrom, prev.from)
            ) {
                dateTo = prev.from;
            }

            latestSelectionRef.current = { from: dateFrom, to: dateTo };
            setHoverDate(null);

            onChange({
                [dateFromName]: dateFrom,
                [dateToName]: dateTo
            });

            if (dateFrom && dateTo) {
                setOpen(false);
            }
        },
        [
            dateFromName,
            dateToName,
            onChange
        ]
    );

    const handleDayMouseEnter = useCallback(
        (date) => {
            if (!isSelectingSecondDate) {
                setHoverDate(null);
                return;
            }

            setHoverDate(checkDate(date) ? date : null);
        },
        [isSelectingSecondDate]
    );

    const handleMonthMouseLeave = useCallback(() => {
        setHoverDate(null);
    }, []);

    // react-datepicker подсвечивает только даты после startDate.
    // Для выбора «сначала ДО» добавляем те же selecting-классы в обратную сторону.
    const getDayClassName = useCallback(
        (date) => {
            if (
                !isSelectingSecondDate ||
                !hoverDate ||
                !checkDate(date) ||
                !isBefore(hoverDate, dateFromValue)
            ) {
                return undefined;
            }

            if (
                !isSameDayOrBefore(hoverDate, date) ||
                !isSameDayOrBefore(date, dateFromValue)
            ) {
                return undefined;
            }

            const classes = ['react-datepicker__day--in-selecting-range'];

            if (isSameDay(date, hoverDate)) {
                classes.push('react-datepicker__day--selecting-range-start');
            }

            if (isSameDay(date, dateFromValue)) {
                classes.push('react-datepicker__day--selecting-range-end');
            }

            return classes.join(' ');
        },
        [isSelectingSecondDate, hoverDate, dateFromValue]
    );

    const handleClose = useCallback(
        () => {
            setOpen(false);
            setHoverDate(null);

            const { from, to } = latestSelectionRef.current;

            if (from && !to) {
                latestSelectionRef.current = { from, to: from };
                onChange({
                    [dateFromName]: from,
                    [dateToName]: from
                });
            } else if (!from && to) {
                latestSelectionRef.current = { from: to, to };
                onChange({
                    [dateFromName]: to,
                    [dateToName]: to
                });
            }
        },
        [
            dateFromName,
            dateToName,
            onChange
        ]
    );

    useEffect(
        () => {
            latestSelectionRef.current = { from: dateFromValue, to: dateToValue };
        },
        [dateFromValue, dateToValue]
    );

    useEffect(
        () => {
            setInputValue(text);
        },
        [text]
    );

    const withMonthYearDropdowns = showMonthDropdown || showYearDropdown;

    return (
        <StyledRangeDatePicker>
            {!!label && <label>{label}</label>}
            <Popup
                open={open}
                basic
                position='bottom left'
                closeOnTriggerMouseLeave={false}
                closeOnPortalMouseLeave={false}
                onClose={handleClose}
                style={{ padding: '0', border: '0', backgroundColor: 'rgba(0,0,0,0)', marginTop: '4px'}}
                trigger={(
                    <ClearableInput
                        readOnly
                        withSort={withSort}
                        sizer={withSort}
                        compact
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={handleTextInput}
                        onFocus={() => setOpen(true)}
                        onClickCapture={e => e.stopPropagation()}
                    />
                )}
            >
                <DatePicker
                    {...props}
                    className={withMonthYearDropdowns ? 'with-month-year-dropdowns' : ''}
                    name="rangeDatePicker"
                    width="auto"
                    hideCalendarIcon
                    startDate={dateFromValue}
                    endDate={dateToValue}
                    selectsRange
                    shouldCloseOnSelect={false}
                    showPopperArrow={false}
                    inline
                    showMonthDropdown={showMonthDropdown}
                    showYearDropdown={showYearDropdown}
                    dropdownMode={dropdownMode}
                    dayClassName={getDayClassName}
                    onDayMouseEnter={handleDayMouseEnter}
                    onMonthMouseLeave={handleMonthMouseLeave}
                    onChange={handleChange}
                />
            </Popup>
        </StyledRangeDatePicker>
    );
};

export default RangeDatePicker;



// WEBPACK FOOTER //
// ./App/components/DatePicker/RangeDatePicker.jsx